import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Availability, Service } from '@acs/shared';
import { saveAvailability, subscribeAvailability } from '../../shared/api/availability.js';
import { updateBusinessSettings, type BusinessSettingsPatch } from '../../shared/api/businesses.js';
import { patchCachedBusiness } from '../../shared/api/businessCache.js';
import type { AvailabilityDoc, BusinessDoc, ServiceDoc } from '../../shared/api/converters.js';
import { useAuth } from '../auth/index.js';
import { keys } from '../../shared/api/keys.js';
import { deleteService, subscribeServices, updateService, upsertService, type ServiceEdit } from '../../shared/api/services.js';
import { listUpcomingForService } from '../../shared/api/appointments.js';
import { useLiveQuery } from '../../shared/api/useLiveQuery.js';
import { subscribeReadiness, type Readiness } from '../../shared/api/readiness.js';

/** Server readiness of the published configuration (doc 33 W9). */
export function useReadiness(businessId: string) {
  return useLiveQuery<Readiness | null>(keys.readiness(businessId), (cb) => subscribeReadiness(businessId, cb));
}

export function useServices(businessId: string) {
  return useLiveQuery<ServiceDoc[]>(keys.services(businessId), (cb) => subscribeServices(businessId, cb));
}

export function useAvailability(businessId: string) {
  return useLiveQuery<AvailabilityDoc | null>(keys.availability(businessId), (cb) =>
    subscribeAvailability(businessId, cb),
  );
}

export function useUpsertService(businessId: string) {
  return useMutation({
    mutationFn: ({ service, id }: { service: Service; id?: string }) =>
      upsertService(businessId, service, id),
  });
}

export function useDeleteService(businessId: string) {
  return useMutation({ mutationFn: (id: string) => deleteService(businessId, id) });
}

export function useUpdateService(businessId: string) {
  return useMutation({
    mutationFn: ({ id, edit }: { id: string; edit: ServiceEdit }) => updateService(businessId, id, edit),
  });
}

/** One-shot check behind Remove (doc 45): upcoming bookings of the service. */
export function useUpcomingForService(businessId: string) {
  return useMutation({ mutationFn: (serviceId: string) => listUpcomingForService(businessId, serviceId) });
}

export function useSaveAvailability(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: Availability) => saveAvailability(businessId, a),
    // Seed the cache with exactly what we persisted, so the wizard's hours step
    // can advance without waiting for the listener to echo it back — otherwise
    // the clamp pulls the owner straight back to Hours (see `patchCachedBusiness`).
    onSuccess: (_res, a) => qc.setQueryData(keys.availability(businessId), a),
  });
}

export function useSaveSettings(businessId: string) {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: (patch: BusinessSettingsPatch) => updateBusinessSettings(businessId, patch),
    onSuccess: (_res, patch) => {
      qc.invalidateQueries({ queryKey: keys.business(businessId) });
      // The wizard reads the business from the OWNER'S LIST, not from
      // `keys.business`, so the invalidation above does nothing for it. Without
      // this the assistant step saves the greeting and then bounces back to
      // itself, because the clamp still sees a business with no greeting.
      patchCachedBusiness(qc, user?.uid ?? '', businessId, patch as Partial<BusinessDoc>);
    },
  });
}
